import {
  useApproveAccessMutation,
  useListAllGrantsQuery,
  useListMyGrantsQuery,
  useRevokeAccessMutation,
} from "@/features/temp-access/api/tempAccessApi";
import { GrantsTable } from "@/features/temp-access/components/GrantsTable";
import { RequestTempAccessModal } from "@/features/temp-access/components/RequestTempAccessModal";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { useHasPermission } from "@/shared/auth/usePermissions";
import { notify } from "@/shared/components/notify";
import { Permission } from "@keystone/contracts/shared";
import { type TempAccessGrant, TempAccessStatus } from "@keystone/contracts/temp-access";
import { ActionIcon, Button, Group, Stack, Tabs, Title, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";
import { FiCheck, FiPlus, FiRefreshCw, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router";

export function TempAccessPage() {
  const { t } = useTranslation("temp-access");
  const canManage = useHasPermission(Permission.ManageUsers);
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("tab") === "manage" && canManage ? "manage" : "mine";

  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const mine = useListMyGrantsQuery();
  const all = useListAllGrantsQuery(canManage ? undefined : skipToken);

  const [approveAccess] = useApproveAccessMutation();
  const [revokeAccess] = useRevokeAccessMutation();

  const activeQuery = activeTab === "manage" ? all : mine;
  const reload = () => void activeQuery.refetch();

  const handleApprove = async (g: TempAccessGrant) => {
    try {
      await approveAccess({ userId: g.userId, grantId: g.id }).unwrap();
      notify.success("temp-access:notifications.approved");
    } catch (e) {
      handleMutationError(e, "temp-access:errors.approveFailed");
    }
  };

  const confirmRevoke = (g: TempAccessGrant) => {
    modals.openConfirmModal({
      title: t("confirm.revoke.title"),
      children: t("confirm.revoke.body", { user: g.userEmail, org: g.orgName }),
      labels: { confirm: t("actions.revoke"), cancel: t("common:actions.cancel") },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await revokeAccess({ userId: g.userId, grantId: g.id }).unwrap();
          notify.success("temp-access:notifications.revoked");
        } catch (e) {
          handleMutationError(e, "temp-access:errors.revokeFailed");
        }
      },
    });
  };

  const manageActions = (g: TempAccessGrant) => (
    <Group gap="xs" justify="flex-end" wrap="nowrap">
      {g.status === TempAccessStatus.Pending && (
        <Tooltip label={t("actions.approve")}>
          <ActionIcon
            variant="light"
            color="green"
            aria-label={t("actions.approve")}
            onClick={() => void handleApprove(g)}
          >
            <FiCheck aria-hidden="true" />
          </ActionIcon>
        </Tooltip>
      )}
      <Tooltip label={t("actions.revoke")}>
        <ActionIcon
          variant="light"
          color="red"
          aria-label={t("actions.revoke")}
          onClick={() => confirmRevoke(g)}
        >
          <FiX aria-hidden="true" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>{t("page.title")}</Title>
        <Group gap="sm" wrap="nowrap">
          <Tooltip label={t("actions.reload")}>
            <ActionIcon
              variant="default"
              size="lg"
              loading={activeQuery.isFetching}
              onClick={reload}
              aria-label={t("actions.reload")}
            >
              <FiRefreshCw aria-hidden="true" />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<FiPlus aria-hidden="true" />} onClick={openModal}>
            {t("actions.request")}
          </Button>
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={(value) => setParams(value ? { tab: value } : {})}>
        <Tabs.List>
          <Tabs.Tab value="mine">{t("tabs.mine")}</Tabs.Tab>
          {canManage && <Tabs.Tab value="manage">{t("tabs.manage")}</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="mine" pt="md">
          <GrantsTable
            records={mine.data ?? []}
            fetching={mine.isLoading || mine.isFetching}
            error={Boolean(mine.error)}
            onRetry={() => void mine.refetch()}
            emptyText={t("empty.mine")}
          />
        </Tabs.Panel>

        {canManage && (
          <Tabs.Panel value="manage" pt="md">
            <GrantsTable
              records={all.data ?? []}
              fetching={all.isLoading || all.isFetching}
              error={Boolean(all.error)}
              onRetry={() => void all.refetch()}
              showUser
              emptyText={t("empty.manage")}
              actions={manageActions}
            />
          </Tabs.Panel>
        )}
      </Tabs>

      <RequestTempAccessModal opened={modalOpened} onClose={closeModal} />
    </Stack>
  );
}
